<template>
  <YapModal :set-is-open="setIsOpen" :wide="state.isScanned" @modal-closed="$emit('modalClosed')">
    <template #title>{{ state.modalTitle }}</template>
    <template #default="{ closeModal }">
      <div v-if="!state.isScanned">
        <div class="flex flex-col items-center justify-center gap-4 py-5">
          <YapIcon name="QR-payment" class="h-20 w-20" />
          <div class="prose dark:prose-invert" v-html="marked(t('webapp.payments.scan.info.description'))"></div>
          <YapButton
            :button-size="ButtonSize.Small"
            :button-style="ButtonStyle.Info"
            :disabled="state.disableTriggerButton"
            outlined
            @click="triggerPushNotification"
          >
            {{ t('webapp.payments.scan.info.new') }}
          </YapButton>
        </div>
      </div>
      <div v-else>
        <PaymentForm
          :payment-order="state.paymentOrder"
          @submitted-and-modal-closed="closeModal"
          v-if="state.paymentOrder?.status === PaymentOrderStatus.draft"
        />
        <div class="flex flex-col items-center justify-center gap-4 py-5" v-else>
          <YapIcon name="checkmark-circle-outline" class="h-20 w-20 text-primary-500" />
          <div
            class="prose dark:prose-invert"
            v-html="marked(t('app.payments.payment_executed.dialog.txt'))"
            v-if="state.paymentOrder?.status === PaymentOrderStatus.executed"
          ></div>
          <div class="prose dark:prose-invert" v-else>
            {{
              t('app.payments.payment_saved.dialog.txt', [
                formatIsoToLocaleString(state.paymentOrder?.paymentOrderData.expectedExecutionTime ?? '')
              ])
            }}
          </div>
        </div>
      </div>
    </template>
  </YapModal>
</template>

<script setup lang="ts">
import { marked } from 'marked';
import { computed, reactive, watch } from 'vue';

import apiGw from '@/services/apiGw';
import { useI18n } from '@/plugins/i18n';
import { SseListener } from '@/utils/general';
import YapIcon from '@/components/YapIcon.vue';
import YapModal from '@/components/YapModal.vue';
import { useUserStore } from '@/store/userStore';
import { formatIsoToLocaleString } from '@/utils/locale';
import { useEventStreamStore } from '@/store/eventStreamStore';
import PaymentForm from '@/components/payments/PaymentForm.vue';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { usePaymentOrdersStore } from '@/store/paymentOrdersStore';
import { PaymentFormData } from '@/components/payments/types/payment';
import { PendingPaymentOrderInfo } from '@/components/payments/types/payment';
import YapButton, { ButtonSize, ButtonStyle } from '@/components/YapButton.vue';
import { PaymentOrderStatus } from '@/components/transactions/types/paymentOrder';

const { t } = useI18n();
const userStore = useUserStore();
const eventStreamStore = useEventStreamStore();
const contextSwitchStore = useContextSwitchStore();
const paymentOrdersStore = usePaymentOrdersStore();

const emit = defineEmits(['modalClosed']);
const props = defineProps<{
  setIsOpen: boolean;
}>();

interface State {
  paymentOrder?: PendingPaymentOrderInfo;
  isScanned: boolean;
  disableTriggerButton: boolean;
  pushNotificationTime?: number;
  fakeForm: PaymentFormData;
  accountUid?: string;
  error?: any;
  modalTitle: string;
}

const state: State = reactive({
  isScanned: false,
  disableTriggerButton: true,
  pushNotificationTime: undefined,
  paymentOrder: undefined,
  fakeForm: {
    beneficiary: {
      uid: undefined,
      name: '',
      iban: '',
      address: undefined,
      unstructuredAddress: undefined
    },
    amount: '',
    currency: 'CHF',
    reference: undefined,
    paymentReason: undefined,
    paymentTime: new Date().toISOString(),
    recurrence: 'single',
    userNote: undefined,
    category: 'general',
    accountUid: undefined,
    paymentType: undefined,
    e2eReference: undefined,
    referenceType: undefined,
    customerTransactionId: undefined
  },
  error: undefined,
  modalTitle: computed(() => {
    if (state.isScanned) {
      if (state.paymentOrder?.status === PaymentOrderStatus.draft) {
        return t('webapp.payments.scan.info.title');
      } else if (state.paymentOrder?.status === PaymentOrderStatus.executed) {
        return t('app.payments.payment_executed.dialog.title');
      } else if (state.paymentOrder?.status === PaymentOrderStatus.accepted) {
        return t('app.payments.payment_saved.dialog.title');
      }
      return t('app.payments.newPayment');
    } else {
      return t('webapp.payments.scan.info.title');
    }
  })
});

watch(
  () => props.setIsOpen,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      triggerPushNotification();
    } else {
      state.isScanned = false;
      state.paymentOrder = undefined;
    }
  }
);

function triggerPushNotification() {
  state.disableTriggerButton = true;
  const agentUid = contextSwitchStore.currentContextUid;
  apiGw.post(`/agents/${agentUid}/notify-qr-scan`);
  state.pushNotificationTime = new Date().getTime();
  setTimeout(() => {
    state.disableTriggerButton = false;
  }, 5000);
}

watch(
  () => eventStreamStore.stale_payment_orders,
  async (newVal) => {
    if (!newVal || !props.setIsOpen) {
      return;
    }
    const newPayemntOrderUids = Object.keys(newVal);
    const paymentOrderUidsToUnstale: string[] = [];
    newPayemntOrderUids.forEach((uid) => {
      paymentOrdersStore
        .getPaymentOrder(`/v2/iban-payment-orders/${uid}`)
        .then((po: PendingPaymentOrderInfo | undefined) => {
          if (po && po.paymentOrderData.requestingAgentUid === userStore.uid) {
            const poCreated = new Date(po.createdAt).getTime();
            /**
             * We only want to do something for payment order that are newer than the push notification time
             * as any other one cannot have been created by the push notification.
             */
            if (
              state.pushNotificationTime &&
              poCreated > state.pushNotificationTime &&
              [PaymentOrderStatus.draft, PaymentOrderStatus.accepted, PaymentOrderStatus.executed].includes(po.status)
            ) {
              state.paymentOrder = po;
              state.isScanned = true;
              paymentOrderUidsToUnstale.push(uid);
            }
          }
        });
    });
    for (const uid of paymentOrderUidsToUnstale) {
      eventStreamStore.unstaleUid(uid, SseListener.PaymentOrder);
    }
  },
  { deep: true }
);
</script>
